import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import UserPicker from '../../components/LeadForm/UserPicker'
import FormHomepage from '../../components/LeadForm/FormHomepage'
import ChoiceStep from '../../components/LeadForm/ChoiceStep'
import ContactStep from '../../components/LeadForm/ContactStep'
import { RegistrationComplete, DuplicateDetected } from '../../components/LeadForm/CompletionScreens'
import { LeadFormPageProps, FormScreen } from '../../components/LeadForm/types'

// Top-level screens the flow can land on. The 'form' screen is the
// unified choice/contact flow — which specific step renders is driven
// by screenIndex + screens[screenIndex].type.
type Screen = 'picker' | 'homepage' | 'form' | 'completed' | 'duplicate'

function screenFromProps(outcome: string, userMode: string): Screen {
  if (outcome === 'completed') return 'completed'
  if (outcome === 'duplicate') return 'duplicate'
  if (userMode === 'none') return 'picker'
  return 'homepage'
}

export default function Show(props: LeadFormPageProps) {
  const { event, users, screens, selected_user, existing_companies, outcome, completed_kind } = props

  const serverScreen = screenFromProps(outcome, selected_user.mode)

  const [screen, setScreen] = useState<Screen>(serverScreen)
  const [prevServerScreen, setPrevServerScreen] = useState(serverScreen)
  const [screenIndex, setScreenIndex] = useState(0)
  const [answers, setAnswers] = useState<Record<number, number[]>>({})
  const [contactValues, setContactValues] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Sync from server when props change (e.g. after submit redirect),
  // but don't fight local navigation once the user clicks away.
  if (serverScreen !== prevServerScreen) {
    setPrevServerScreen(serverScreen)
    setScreen(serverScreen)
  }

  const totalScreens = screens.length
  const selectedUserName =
    selected_user.mode === 'user' ? selected_user.name : selected_user.mode === 'self_service' ? 'Self Service' : ''

  const currentScreen: FormScreen | undefined = screens[screenIndex]
  const isFirstScreen = screenIndex === 0
  const isLastScreen = screenIndex === totalScreens - 1

  // ── Navigation handlers ────────────────────────────────────────────

  const resetFlowState = () => {
    setScreenIndex(0)
    setAnswers({})
    setContactValues({})
  }

  const goToHomepage = () => {
    resetFlowState()
    setScreen('homepage')
  }

  const startForm = () => {
    resetFlowState()
    if (screens.length === 0) {
      // Defensive — should never happen given the contact step always exists,
      // but if an event somehow has no screens at all, fall back to homepage.
      setScreen('homepage')
    } else {
      setScreen('form')
    }
  }

  const handleAnswerChange = (stepId: number, optionIds: number[]) => {
    setAnswers((prev) => ({ ...prev, [stepId]: optionIds }))
  }

  const handleContactChange = (key: string, value: string) => {
    setContactValues((prev) => ({ ...prev, [key]: value }))
  }

  const handleNext = () => {
    if (isLastScreen) {
      handleSubmit()
    } else {
      setScreenIndex(screenIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (screenIndex > 0) {
      setScreenIndex(screenIndex - 1)
    }
  }

  const handleSubmit = () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    router.post(
      `/${event.slug}/submit`,
      {
        contact_values: contactValues,
        answers: Object.fromEntries(
          Object.entries(answers).map(([k, v]) => [k, v.map(String)]),
        ),
      },
      {
        onFinish: () => setIsSubmitting(false),
      },
    )
  }

  const handleConfirmUpdate = () => {
    setIsSubmitting(true)
    router.post(`/${event.slug}/confirm_update`, {}, { onFinish: () => setIsSubmitting(false) })
  }

  const handleCancelUpdate = () => {
    router.post(`/${event.slug}/cancel_update`)
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <>
      <Head title={`${event.title} — Lead Capture`} />

      {screen === 'picker' && <UserPicker eventSlug={event.slug} users={users} />}

      {screen === 'homepage' && (
        <FormHomepage
          eventSlug={event.slug}
          eventTitle={event.title}
          selectedUser={selected_user}
          onGetStarted={startForm}
        />
      )}

      {screen === 'form' && currentScreen && currentScreen.type === 'choice' && (
        <ChoiceStep
          step={currentScreen}
          stepNumber={screenIndex + 1}
          totalSteps={totalScreens}
          isFirstStep={isFirstScreen}
          isLastStep={isLastScreen}
          selectedOptionIds={answers[currentScreen.id] || []}
          onChange={(opts) => handleAnswerChange(currentScreen.id, opts)}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onBackToHomepage={goToHomepage}
          selectedUserName={selectedUserName}
          isSubmitting={isSubmitting}
        />
      )}

      {screen === 'form' && currentScreen && currentScreen.type === 'contact' && (
        <ContactStep
          step={currentScreen}
          stepNumber={screenIndex + 1}
          totalSteps={totalScreens}
          isFirstStep={isFirstScreen}
          isLastStep={isLastScreen}
          values={contactValues}
          existingCompanies={existing_companies}
          onChange={handleContactChange}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onBackToHomepage={goToHomepage}
          selectedUserName={selectedUserName}
          isSubmitting={isSubmitting}
        />
      )}

      {screen === 'completed' && (
        <RegistrationComplete
          kind={completed_kind || 'created'}
          onNewVisitor={startForm}
          onBackToHomepage={goToHomepage}
        />
      )}

      {screen === 'duplicate' && (
        <DuplicateDetected onCancel={handleCancelUpdate} onConfirm={handleConfirmUpdate} isSubmitting={isSubmitting} />
      )}
    </>
  )
}
